import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 22000-22999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const portalErrors = {
  // 22xxx is a fresh block for the Portal module (packages/server/portal/). It carries BOTH splits:
  // 22001-22008 came off RESOURCE_NOT_FOUND (9003), 22009-22018 off SOMETHING_WENT_WRONG (9000).
  // Note what is NOT here: a portal-not-found code. All 5 such sites reuse PORTAL_NOT_FOUND (7046),
  // whose canonical message is already character-identical to theirs.
  //
  // ── from RESOURCE_NOT_FOUND ──
  // `addPortalProfile`: the acting user's own `accounts` row did not resolve. Its own sentence, so
  // separate from PORTAL_ACCOUNT_NOT_FOUND below and from the 7079/7080 session-user codes.
  PORTAL_SESSION_ACCOUNT_NOT_FOUND: {
    errorCode: 22001,
    errorMessage: t("Session user account not found"),
    isDynamic: false,
  },
  // The acting user's `accounts` row did not resolve on a portal-settings write. 4 call sites.
  PORTAL_ACCOUNT_NOT_FOUND: {
    errorCode: 22002,
    errorMessage: t("Your account could not be found"),
    isDynamic: false,
  },
  // A portal role ("profile") in `portals.roles[]` could not be resolved.
  PORTAL_PROFILE_NOT_FOUND: {
    errorCode: 22003,
    errorMessage: t("Profile does not exist"),
    isDynamic: false,
  },
  // `addPortalProfile`: the role named by `copyRole` (the duplicate-profile source) is gone.
  PORTAL_PROFILE_TO_DUPLICATE_NOT_FOUND: {
    errorCode: 22004,
    errorMessage: t("Profile to duplicate does not exist"),
    isDynamic: false,
  },
  // `deletePortalProfile`: the role no longer exists on this portal. Formally a matchedCount guard,
  // but its sentence is a genuine user-facing not-found, so it is split rather than left generic.
  PORTAL_PROFILE_NO_LONGER_EXISTS: {
    errorCode: 22005,
    errorMessage: t("This profile no longer exists in this Portal."),
    isDynamic: false,
  },
  // A `portals.statusList[]` entry could not be resolved.
  PORTAL_STATUS_NOT_FOUND: {
    errorCode: 22006,
    errorMessage: t("Status not found."),
    isDynamic: false,
  },
  // A `portals.revisionSeriesList[]` entry could not be resolved. 2 call sites.
  PORTAL_REVISION_SERIES_NOT_FOUND: {
    errorCode: 22007,
    errorMessage: t("Revision series not found"),
    isDynamic: false,
  },
  // A `portals.nomenclatureTemplatesList[]` entry could not be resolved. 2 call sites.
  PORTAL_NOMENCLATURE_TEMPLATE_NOT_FOUND: {
    errorCode: 22008,
    errorMessage: t("Nomenclature template not found"),
    isDynamic: false,
  },
  // ── from SOMETHING_WENT_WRONG ──
  // The built-in default portal role cannot be modified. 3 call sites. Portal-scoped, so deliberately
  // not PROJECT_DEFAULT_ROLE_PROTECTED (20005).
  PORTAL_DEFAULT_ROLE_PROTECTED: {
    errorCode: 22009,
    errorMessage: t("Default Role cannot be modified"),
    isDynamic: false,
  },
  // A portal role still assigned to at least one user cannot be deleted. Portal-scoped counterpart of
  // PROJECT_ROLE_IN_USE (20006).
  PORTAL_ROLE_IN_USE: {
    errorCode: 22010,
    errorMessage: t("Role cannot be deleted as it is assigned to at least one user"),
    isDynamic: false,
  },
  // `changeUserPortalProfile`: the seat cannot be assigned because the user belongs to more than one
  // space and project. 2 call sites (the >1-project and >1-space branches share one sentence).
  PORTAL_SEAT_ASSIGNMENT_BLOCKED: {
    errorCode: 22011,
    errorMessage: t("Cannot assign this seat since the user is part of more than one space and one project."),
    isDynamic: false,
  },
  // A system-generated portal status cannot be deleted.
  PORTAL_STATUS_SYSTEM_GENERATED: {
    errorCode: 22012,
    errorMessage: t("The selected status is system generated and can not be deleted"),
    isDynamic: false,
  },
  // The portal status is referenced by a review flow, so it cannot be deleted.
  PORTAL_STATUS_IN_USE_BY_REVIEW_FLOW: {
    errorCode: 22013,
    errorMessage: t("The status is being used in a reviewFlow"),
    isDynamic: false,
  },
  // Custom sequence types are not enabled for this portal. 2 call sites.
  PORTAL_CUSTOM_SEQUENCE_NOT_ENABLED: {
    errorCode: 22014,
    errorMessage: t("Custom sequence types are not enabled for this portal"),
    isDynamic: false,
  },
  // A revision series already in use cannot have its pattern changed.
  PORTAL_REVISION_SERIES_PATTERN_LOCKED: {
    errorCode: 22015,
    errorMessage: t("Pattern cannot be changed because this series is in use"),
    isDynamic: false,
  },
  // The default revision series cannot be deleted.
  PORTAL_DEFAULT_SERIES_UNDELETABLE: {
    errorCode: 22016,
    errorMessage: t("Default series cannot be deleted"),
    isDynamic: false,
  },
  // A revision series associated with files or folders cannot be deleted.
  PORTAL_REVISION_SERIES_IN_USE: {
    errorCode: 22017,
    errorMessage: t("Cannot delete: series is associated with files or folders"),
    isDynamic: false,
  },
  // A nomenclature template assigned to folders cannot be deleted.
  PORTAL_NOMENCLATURE_TEMPLATE_IN_USE: {
    errorCode: 22018,
    errorMessage: t("Cannot delete: template is assigned to folders"),
    isDynamic: false,
  },
  // 22019-22028 is the portal module's SOMETHING_WENT_WRONG (9000) split. All but one are SAML
  // metadata/URL validation; the remaining site interpolates a file extension and keeps the standing
  // dynamic-message deferral.
  // 22019-22028 — the portal module's split.
  PORTAL_SAML_URL_INVALID: {
    errorCode: 22019,
    errorMessage: t("Invalid URL format"),
    isDynamic: false,
  },
  PORTAL_SAML_URL_MUST_BE_HTTPS: {
    errorCode: 22020,
    errorMessage: t("Only HTTPS URLs are allowed"),
    isDynamic: false,
  },
  // SSRF guard: the metadata URL resolves to an internal/private address.
  PORTAL_SAML_URL_PRIVATE_NOT_ALLOWED: {
    errorCode: 22021,
    errorMessage: t("Internal/private URLs are not allowed"),
    isDynamic: false,
  },
  PORTAL_SAML_METADATA_FETCH_FAILED: {
    errorCode: 22022,
    errorMessage: t("Failed to fetch metadata from the provided URL"),
    isDynamic: false,
  },
  // 2 call sites (declared length, then actual bytes read).
  PORTAL_SAML_METADATA_RESPONSE_TOO_LARGE: {
    errorCode: 22023,
    errorMessage: t("Metadata response too large (max 5MB)"),
    isDynamic: false,
  },
  PORTAL_SAML_METADATA_INVALID_XML: {
    errorCode: 22024,
    errorMessage: t("The URL did not return valid SAML metadata XML"),
    isDynamic: false,
  },
  // The pasted-XML path has its own, smaller cap than the fetched-URL path above.
  PORTAL_SAML_METADATA_XML_TOO_LARGE: {
    errorCode: 22025,
    errorMessage: t("Metadata XML is too large (max 1MB)"),
    isDynamic: false,
  },
  PORTAL_SAML_LOGOUT_URL_MUST_BE_HTTPS: {
    errorCode: 22026,
    errorMessage: t("Logout URL must use HTTPS protocol"),
    isDynamic: false,
  },
  PORTAL_SAML_LOGOUT_URL_INVALID: {
    errorCode: 22027,
    errorMessage: t("Logout URL is not a valid URL"),
    isDynamic: false,
  },
  // 22029-22032 came off NO_CHANGES_APPLIED (9005) — all name-uniqueness guards.
  PORTAL_MODULE_NAME_EXISTS: {
    errorCode: 22029,
    errorMessage: t("A module with this name already exists."),
    isDynamic: false,
  },
  PORTAL_DEFAULT_MODULE_NAME_EXISTS: {
    errorCode: 22030,
    errorMessage: t("There is already a default module with the same name"),
    isDynamic: false,
  },
  PORTAL_REVISION_SERIES_NAME_EXISTS: {
    errorCode: 22031,
    errorMessage: t("A revision series with this name already exists"),
    isDynamic: false,
  },
  PORTAL_NOMENCLATURE_TEMPLATE_NAME_EXISTS: {
    errorCode: 22032,
    errorMessage: t("A nomenclature template with this name already exists"),
    isDynamic: false,
  },
  // 22033-22038 came off VALIDATION_FAILED (9004); the last is the SAML SP consumer's.
  PORTAL_MODULE_NAME_AND_DESCRIPTION_REQUIRED: {
    errorCode: 22033,
    errorMessage: t("Module name and description are required."),
    isDynamic: false,
  },
  PORTAL_SSO_METADATA_AND_MAPPINGS_REQUIRED: {
    errorCode: 22034,
    errorMessage: t("Metadata and attribute mappings are required when SSO is enabled"),
    isDynamic: false,
  },
  PORTAL_ENFORCED_MODE_INVALID: {
    errorCode: 22035,
    errorMessage: t("Invalid enforced mode. Must be 'any', 'email', or 'qr'."),
    isDynamic: false,
  },
  // 22036 (PORTAL_METADATA_JSON_INVALID) is retired: nothing sends it. Do not reuse the number.
  PORTAL_TEMPLATE_NAME_REQUIRED: {
    errorCode: 22037,
    errorMessage: t("Template name is required"),
    isDynamic: false,
  },
  SAML_RESPONSE_INVALID: {
    errorCode: 22038,
    errorMessage: t("SAML response invalid"),
    isDynamic: false,
  },
  // 22040-22042: the portal role/seat refusals. Same sentences as the projects block 20032-20033, but owned by this module so each block stays self-contained.
  PORTAL_NOT_ENOUGH_SEATS: {
    errorCode: 22040,
    errorMessage: t("This product cannot be assigned to this user because there aren't enough seats."),
    isDynamic: false,
  },
  PORTAL_USER_ROLE_CHANGE_NOT_ALLOWED: {
    errorCode: 22041,
    errorMessage: t("You are not allowed to change role of this user"),
    isDynamic: false,
  },
  PORTAL_USER_PROMOTE_NOT_ALLOWED: {
    errorCode: 22042,
    errorMessage: t("You are not allowed to promote this user to higher rank role"),
    isDynamic: false,
  },
  // 22043-22058 — the portal file-type management feature (insert/update/deleteFileType), which
  // arrived from staging un-migrated: every refusal below sent its sentence as an inline
  // `errorMessage`. Sentences are staging's own, verbatim; only the delivery changed to an errorCode.
  PORTAL_FILE_EXTENSION_REQUIRED: {
    errorCode: 22043,
    errorMessage: t("Extension is required."),
    isDynamic: false,
  },
  PORTAL_FILE_EXTENSION_PERIOD_ONLY: {
    errorCode: 22044,
    errorMessage: t("Enter one or more characters after the period."),
    isDynamic: false,
  },
  PORTAL_FILE_EXTENSION_MISSING_PERIOD: {
    errorCode: 22045,
    errorMessage: t("Extension must start with a period, e.g. .rvt"),
    isDynamic: false,
  },
  PORTAL_FILE_EXTENSION_CONTAINS_SPACES: {
    errorCode: 22046,
    errorMessage: t("Extension can't contain spaces."),
    isDynamic: false,
  },
  PORTAL_FILE_EXTENSION_MULTIPLE_PERIODS: {
    errorCode: 22047,
    errorMessage: t("Enter a single extension, with one leading period."),
    isDynamic: false,
  },
  PORTAL_FILE_EXTENSION_NON_ALPHANUMERIC: {
    errorCode: 22048,
    errorMessage: t("Use letters and numbers only after the period."),
    isDynamic: false,
  },
  PORTAL_FILE_EXTENSION_TOO_LONG: {
    errorCode: 22049,
    errorMessage: t("Extension can't be longer than 10 characters."),
    isDynamic: false,
  },
  PORTAL_FILE_EXTENSION_BLOCKED: {
    errorCode: 22050,
    errorMessage: t("{{extension}} is an executable or script type and can't be added to a portal for security reasons."),
    isDynamic: true,
    dynamicParams: ["extension"],
  },
  PORTAL_FILE_TYPE_DUPLICATE_ACTIVE: {
    errorCode: 22051,
    errorMessage: t("This extension is already in the list."),
    isDynamic: false,
  },
  PORTAL_FILE_TYPE_DUPLICATE_DISABLED: {
    errorCode: 22052,
    errorMessage: t("This extension already exists as a disabled file type. Enable it from the Disabled tab."),
    isDynamic: false,
  },
  PORTAL_FILE_TYPE_DEFAULT_ENGINE_REQUIRED: {
    errorCode: 22053,
    errorMessage: t("Default engine is required."),
    isDynamic: false,
  },
  // Dynamic on the CONSTANT `MAX_PORTAL_FILE_TYPES`: the template stays one catalog key even if the
  // cap is raised, rather than baking today's number into the sentence.
  PORTAL_FILE_TYPE_LIMIT_REACHED: {
    errorCode: 22054,
    errorMessage: t("A portal can have at most {{max}} file types."),
    isDynamic: true,
    dynamicParams: ["max"],
  },
  PORTAL_FILE_TYPE_NOT_FOUND: {
    errorCode: 22055,
    errorMessage: t("File type not found"),
    isDynamic: false,
  },
  PORTAL_FILE_TYPE_SINGLE_EDIT_ONLY: {
    errorCode: 22056,
    errorMessage: t("Only one file type can be edited at a time."),
    isDynamic: false,
  },
  PORTAL_SYSTEM_FILE_TYPE_TOGGLE_ONLY: {
    errorCode: 22057,
    errorMessage: t("System file types can only be enabled or disabled."),
    isDynamic: false,
  },
  PORTAL_SYSTEM_FILE_TYPE_NOT_DELETABLE: {
    errorCode: 22058,
    errorMessage: t("System file types cannot be deleted"),
    isDynamic: false,
  },
  // 22059-22061 arrived with staging's sign-in methods configuration (CUBE-111). All three were
  // VALIDATION_FAILED plus prose; the prose IS the whole instruction here, so each needs its own code.
  PORTAL_SIGNIN_METHOD_NONE_SELECTED: {
    errorCode: 22059,
    errorMessage: t("Select at least one sign-in method, or turn the restriction off."),
    isDynamic: false,
  },
  // `methods` is the already-joined list the server built; it is a list of method NAMES, not
  // translatable prose, so interpolating it after t() is safe.
  PORTAL_SIGNIN_METHODS_UNAVAILABLE: {
    errorCode: 22060,
    errorMessage: t("These sign-in methods are not available on this portal: {{methods}}."),
    isDynamic: true,
    dynamicParams: ["methods"],
  },
  PORTAL_SIGNIN_METHOD_WOULD_LOCK_OUT_CALLER: {
    errorCode: 22061,
    errorMessage: t(
      "Link one of the selected sign-in providers to your own account (Profile > Security) before restricting sign-in to it, or you would lock yourself out of this portal.",
    ),
    isDynamic: false,
  },
  // `updateSsoConfig` turning SSO off while the stored sign-in policy lists no method the portal can run without its
  // IdP. Filed beside the sign-in lock-out refusals above, out of numeric order.
  PORTAL_SSO_DISABLE_WOULD_LOCK_OUT: {
    errorCode: 22065,
    errorMessage: t(
      "Allow another sign-in method under Sign-in Methods before turning SSO off, or nobody could sign in to this portal.",
    ),
    isDynamic: false,
  },
  // The CUBE AI seat map accepts only Full Seat or No Seat — there is no half seat for it.
  PORTAL_AI_SEAT_VALUE_INVALID: {
    errorCode: 22062,
    errorMessage: t("CUBE AI seats are Full Seat or No Seat only."),
    isDynamic: false,
  },
  MODULE_NOT_FOUND_OR_NO_PERMISSION: {
    errorCode: 22063,
    errorMessage: t("{{moduleName}} does not exists or you do not have permission to view it"),
    isDynamic: true,
    dynamicParams: ["moduleName"],
  },
  PORTAL_STATUS_NAME_EXISTS: {
    errorCode: 22064,
    errorMessage: t("A status with this name already exists in this category"),
    isDynamic: false,
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
